// SH · Share card (QR5_Share; QR5b_ShareStreak, QR5c_ShareWeek). 9:16 preview: the buddy's key art as the hero,
// fading into their dark, then the line. card = the reward's done line (+ "Hide what the card was"), streak = days in
// a row, week = this week's numbers. "Share" uses the share sheet on phones (@capacitor/share via native.ts) and Web
// Share / a download elsewhere; "Save image" downloads the PNG (phones: the share sheet, which can save it).
// Admin events: share_card when it opens, and when an image was saved or shared (not when the sheet was cancelled).
import { useEffect, useState } from 'preact/hooks';
import { C } from '@shared/content/characters';
import { LINES } from '@shared/content/lines';
import { stampDate, taskShort } from '@shared/rules';
import { useApp } from '../../store';
import { back, useParams } from '../../router';
import { isNativeMobile, share } from '../../native';
import { Screen, XCard, Fit, IconButton, Icon, PrimaryButton, SecondaryButton } from '../../components';
import { rgba } from '../../theme';
import { say } from '../../lib/say';
import { track } from '../../lib/analytics';
import { DAY_ONE } from '../../content/onboarding';
import { SHARE_WEEK, word } from './copy';
import { dayOf } from './util';
import { quoteSize, renderShare, type ShareSpec } from './shareImage';
import s from './ShareScreen.module.css';

type Kind = 'card' | 'streak' | 'week';

export function ShareScreen() {
  const st = useApp((x) => x.state);
  const params = useParams();
  const [hide, setHide] = useState(false);
  const [busy, setBusy] = useState<'save' | 'share' | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const kind: Kind = params.mode === 'streak' || params.mode === 'week' ? params.mode : 'card';
  useEffect(() => { track('share_card', { variant: kind, hideTask: false, action: 'open' }); }, []);
  if (!st) return null;

  const ch = st.ch;
  const c = C[ch];
  const rw = st.reward;
  const recent = st.collection.recent[0];
  const title = rw?.title || recent?.title || '';
  const task = taskShort(title);
  const date = stampDate(rw ? dayOf(rw.doneAt, st.user.tz) : recent?.day || st.today);
  const streak = (kind === 'streak' && rw?.streakTo) || st.streak.count;
  const year = st.today.slice(0, 4);
  const doneLine = rw?.line || say(LINES[ch].signature, { n: c.name, task, year });
  // Hidden: the buddy's signature done line about "The Thing" (no task in it). The server's line can name the task in
  // any wording, so it is never used when hidden.
  const hiddenLine = say(LINES[ch].signature.replace(/\{Task\}/g, 'The Thing'), { n: c.name, year });
  const week = st.week;
  const lit = week?.lit || 0;
  const line = kind === 'card' ? (hide ? hiddenLine : doneLine)
    : kind === 'streak' ? (streak === 1 ? DAY_ONE[ch] : say(LINES[ch].shareStreak, { n: c.name, k: streak }))
    : say(SHARE_WEEK[ch], { n: c.name, k: `${word(lit)} ${lit === 1 ? 'day' : 'days'}` });
  const qSize = quoteSize(`“${line}”`);
  const quote = `“${line}”`;
  const signature = `— ${c.name}, on Xiaoman`;
  const ticketTitle = hide ? 'Something I kept putting off' : task;
  const weekTitle = `My week with ${c.name}`;
  const weekStats = [
    { n: String(week?.cards ?? 0), t: week?.cards === 1 ? 'card done' : 'cards done' },
    { n: `${week?.lit ?? 0} of 7`, t: 'days lit' },
    { n: String(week?.onTime ?? 0), t: 'on time' },
  ];
  const spec: ShareSpec = { ch, kind, quote, signature, streak, ticket: { label: date, title: ticketTitle }, weekTitle, weekStats };
  const daysWord = streak === 1 ? 'day' : 'days';
  const aria = kind === 'card' ? `Share card: ${line}` : kind === 'streak' ? `Share card: ${streak} ${daysWord} in a row` : `Share card: your week with ${c.name}`;
  const fileName = `xiaoman-${kind}-${st.today}.png`;

  const run = async (how: 'save' | 'share') => {
    if (busy) return;
    setBusy(how); setStatus(null);
    const went = () => track('share_card', { variant: kind, hideTask: kind === 'card' && hide, action: how });
    try {
      const blob = await renderShare(spec);
      const file = { blob, name: fileName };
      if (how === 'share' || isNativeMobile()) {
        const r = await share({ title: weekTitle, text: line, file });
        setStatus(r === 'shared' ? 'Shared.' : r === 'saved' ? 'Saved to your downloads.' : r === 'cancelled' ? null : "Couldn't open sharing here. Try Save image.");
        if (r === 'shared' || r === 'saved') went();
      } else {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob); a.download = fileName;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 4000);
        setStatus('Saved to your downloads.');
        went();
      }
    } catch (e) {
      console.warn(e);
      setStatus("Couldn't make the image. Try again.");
    } finally { setBusy(null); }
  };

  return (
    <Screen theme={ch} id="SH" bg="#0A0908">
      <header class={s.head}>
        <IconButton icon="back" label="Back" size={44} iconSize={20} onClick={() => back(params.from === 'U3' ? 'U3' : 'T')} style={{ background: 'rgba(245,241,234,0.08)' }} />
        <h1 class={s.h1}>Share</h1>
      </header>

      <Fit w={270} h={480} min={0.55} class={s.fit}>
        <div class={s.card} role="img" aria-label={aria}>
          <img src={c.assets.key} alt="" class={s.art} />
          <div class={s.fade} aria-hidden="true" style={{ background: `linear-gradient(180deg, rgba(6,5,4,0.5) 0%, rgba(6,5,4,0) 15%, rgba(6,5,4,0) 40%, ${rgba(c.bg, 0.82)} 66%, ${c.bg} 80%)` }} />
          <div class={s.topRow} aria-hidden="true">
            <span class={s.mark}>XIAOMAN</span>
            {kind !== 'streak' && <span class={s.streakChip}><Icon name="flame" size={14} strokeWidth={1.6} color="var(--acc)" fill="var(--acc)" /><span>{`${streak}-day streak`}</span></span>}
          </div>
          {kind === 'card' && (
            <>
              <div class={s.quoteTop} aria-hidden="true">
                <span class={s.quoteLead} />
                <div class={s.quoteBody}>
                  <p class={s.quote} style={{ fontSize: qSize }}>{quote}</p>
                  <span class={s.sig}>{signature}</span>
                </div>
                <span class={s.quoteFill} />
              </div>
              <div class={s.ticket} aria-hidden="true">
                <XCard ch={ch} face="front" w={178} h={96} fit="none" label={date} title={ticketTitle} stamped stamp="DONE" foot="" tilt={-3} />
              </div>
            </>
          )}
          {kind === 'streak' && (
            <>
              <div class={s.big} aria-hidden="true">
                <svg viewBox="0 0 24 24" style={{ width: 62, height: 70, fill: 'var(--acc)', stroke: '#FFE7A8', strokeWidth: 0.6, strokeLinejoin: 'round', filter: 'drop-shadow(0 0 16px var(--acc))' }}>
                  <path d="M12 3c1 3.5 5 5.5 5 10a5 5 0 0 1-10 0c0-2.2 1.2-3.6 2.4-4.6.2 1.6 1 2.6 2.1 2.9C11 9 11.2 6 12 3z" />
                </svg>
                <span class={s.bigN}>{streak}</span>
                <span class={s.bigT}>{daysWord}<br />in a row</span>
              </div>
              <div class={s.quoteBottom} aria-hidden="true"><p class={s.quoteSm}>{quote}</p><span class={s.sig}>{signature}</span></div>
            </>
          )}
          {kind === 'week' && (
            <>
              <p class={s.weekTitle} aria-hidden="true">{weekTitle}</p>
              <div class={s.stats} aria-hidden="true">
                {weekStats.map((x) => <div key={x.t} class={s.stat}><span class={s.statN}>{x.n}</span><span class={s.statT}>{x.t}</span></div>)}
              </div>
              <div class={s.quoteBottom} aria-hidden="true"><p class={s.quoteSm}>{quote}</p><span class={s.sig}>{signature}</span></div>
            </>
          )}
        </div>
      </Fit>

      <div class={s.controls}>
        {kind === 'card' && (
          <button type="button" role="switch" aria-checked={hide} class={s.switch} onClick={() => setHide(!hide)}>
            <span class={s.switchText}><span class={s.switchTitle}>Hide what the card was</span><span class={s.switchSub}>Shows "The Thing" instead</span></span>
            <span class={s.track} data-on={hide ? 'true' : 'false'} aria-hidden="true"><span class={s.knob} /></span>
          </button>
        )}
        <div class={s.btns}>
          <SecondaryButton h={56} grow={1} busy={busy === 'save'} onClick={() => void run('save')}>Save image</SecondaryButton>
          <PrimaryButton h={56} grow={1.4} icon="share" iconFill={false} busy={busy === 'share'} onClick={() => void run('share')} data-cta="share">Share</PrimaryButton>
        </div>
        <p class={s.status} role="status" aria-live="polite">{status || ''}</p>
      </div>
    </Screen>
  );
}
